/**
 * SELECT COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass morphing on interaction
 * - "Lift into glass" effect on focus
 * - Custom dropdown with glass styling
 * - Chevron animation on open
 * - Smooth spring animations
 */

import { useState, useRef, useEffect, forwardRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '../../lib/utils'

interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  description?: string
}

interface SelectProps {
  label?: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  required?: boolean
  error?: string
  hint?: string
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  searchable?: boolean
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  required = false,
  error,
  hint,
  disabled = false,
  size = 'md',
  searchable = false
}, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find(opt => opt.value === value)

  const filteredOptions = searchable && searchQuery
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex(i =>
            i < filteredOptions.length - 1 ? i + 1 : 0
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex(i =>
            i > 0 ? i - 1 : filteredOptions.length - 1
          )
          break
        case 'Enter':
          e.preventDefault()
          const option = filteredOptions[highlightedIndex]
          if (option && !option.disabled) {
            onChange(option.value)
            setIsOpen(false)
            setSearchQuery('')
          }
          break
        case 'Escape':
          setIsOpen(false)
          setSearchQuery('')
          break
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, highlightedIndex, filteredOptions, onChange])

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(0)
  }, [searchQuery])

  const sizeClasses = {
    sm: 'px-3 py-2 text-subheadline',
    md: 'px-4 py-3 text-body',
    lg: 'px-5 py-4 text-headline'
  }

  // Liquid Glass spring animation config
  const springConfig = {
    type: "spring",
    damping: 25,
    stiffness: 300,
    mass: 0.8
  }

  return (
    <div ref={containerRef} className="relative space-y-2">
      {/* Label */}
      {label && (
        <label className="block text-subheadline font-semibold text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
          {label}
          {required && <span className="text-red ml-1">*</span>}
        </label>
      )}

      {/* Select Button with "lift into glass" animation */}
      <motion.button
        ref={ref}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-3',
          sizeClasses[size],
          // Liquid Glass material
          'liquid-material-regular liquid-lens rounded-xl',
          // Border
          error
            ? 'ring-2 ring-red/30 dark:ring-red-dark/30'
            : 'border border-black/[0.06] dark:border-white/[0.06]',
          // Shadow
          'shadow-glass-sm dark:shadow-dark-glass-sm',
          // Focus state
          'focus:outline-none',
          // Disabled
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        animate={{
          scale: isOpen ? 1.01 : 1,
          y: isOpen ? -1 : 0,
          boxShadow: isOpen
            ? '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 3px rgba(0, 122, 255, 0.12)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 400
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={cn(
          'truncate tracking-tight',
          selectedOption
            ? 'text-light-text-primary dark:text-dark-text-primary'
            : 'text-light-text-tertiary dark:text-dark-text-tertiary'
        )}>
          {selectedOption?.label || placeholder}
        </span>

        {/* Animated Chevron with spring */}
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", damping: 15, stiffness: 300 }}
        >
          <ChevronDown
            className={cn(
              "w-5 h-5 flex-shrink-0 transition-colors duration-fast",
              isOpen
                ? "text-blue dark:text-blue-dark"
                : "text-light-text-tertiary dark:text-dark-text-tertiary"
            )}
            strokeWidth={2}
          />
        </motion.div>
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={springConfig}
            className={cn(
              'absolute z-50 w-full mt-2',
              'liquid-material-thick liquid-lens',
              'rounded-2xl overflow-hidden',
              'shadow-liquid-lg dark:shadow-dark-liquid-lg',
              'border border-black/[0.06] dark:border-white/[0.06]'
            )}
            role="listbox"
          >
            {/* Search Input */}
            {searchable && (
              <div className="p-2 border-b border-light-separator dark:border-dark-separator">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className={cn(
                    'w-full px-3 py-2 text-subheadline',
                    'bg-transparent',
                    'text-light-text-primary dark:text-dark-text-primary',
                    'placeholder:text-light-text-quaternary dark:placeholder:text-dark-text-quaternary',
                    'focus:outline-none',
                    'tracking-tight'
                  )}
                />
              </div>
            )}

            {/* Options List */}
            <div className="max-h-64 overflow-y-auto scrollbar-glass py-1">
              {filteredOptions.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-4 py-3 text-center text-subheadline text-light-text-tertiary dark:text-dark-text-tertiary"
                >
                  No options found
                </motion.div>
              ) : (
                filteredOptions.map((option, index) => (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      if (!option.disabled) {
                        onChange(option.value)
                        setIsOpen(false)
                        setSearchQuery('')
                      }
                    }}
                    disabled={option.disabled}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-4 py-3 text-left relative',
                      // Selected state
                      option.value === value && 'bg-blue-glass dark:bg-blue-glass',
                      // Disabled
                      option.disabled && 'opacity-50 cursor-not-allowed'
                    )}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      backgroundColor: highlightedIndex === index && option.value !== value
                        ? 'rgba(0, 0, 0, 0.04)'
                        : option.value === value
                          ? 'rgba(0, 122, 255, 0.1)'
                          : 'transparent'
                    }}
                    transition={{
                      opacity: { delay: index * 0.02, duration: 0.2 },
                      x: { delay: index * 0.02, type: "spring", damping: 20, stiffness: 300 },
                      backgroundColor: { duration: 0.15 }
                    }}
                    whileHover={!option.disabled ? {
                      backgroundColor: option.value === value
                        ? 'rgba(0, 122, 255, 0.15)'
                        : 'rgba(0, 0, 0, 0.06)'
                    } : undefined}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className={cn(
                        'text-body tracking-tight',
                        option.value === value
                          ? 'text-blue dark:text-blue-dark font-semibold'
                          : 'text-light-text-primary dark:text-dark-text-primary'
                      )}>
                        {option.label}
                      </span>
                      {option.description && (
                        <span className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                          {option.description}
                        </span>
                      )}
                    </div>

                    {/* Check mark for selected with animation */}
                    <AnimatePresence>
                      {option.value === value && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ type: "spring", damping: 15, stiffness: 300 }}
                        >
                          <Check
                            className="w-5 h-5 text-blue dark:text-blue-dark flex-shrink-0"
                            strokeWidth={2.5}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <p className="text-caption1 text-red dark:text-red-dark font-medium tracking-tight">
          {error}
        </p>
      )}

      {/* Hint */}
      {hint && !error && (
        <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary tracking-tight">
          {hint}
        </p>
      )}
    </div>
  )
})

Select.displayName = 'Select'

/**
 * NativeSelect - Simpler native select for forms
 */
interface NativeSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string
  options: SelectOption[]
  error?: string
  hint?: string
  onChange: (value: string) => void
}

export function NativeSelect({
  label,
  options,
  error,
  hint,
  onChange,
  required,
  disabled,
  className,
  ...props
}: NativeSelectProps) {
  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-subheadline font-semibold text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
          {label}
          {required && <span className="text-red ml-1">*</span>}
        </label>
      )}

      <select
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className={cn(
          'w-full px-4 py-3 text-body',
          'liquid-material-regular liquid-lens rounded-xl',
          'text-light-text-primary dark:text-dark-text-primary tracking-tight',
          'shadow-glass-sm dark:shadow-dark-glass-sm',
          'focus:outline-none focus:shadow-glass-lg dark:focus:shadow-dark-glass-lg',
          'focus:scale-[1.01]',
          'transition-all duration-base ease-liquid-out',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'appearance-none cursor-pointer',
          // Custom arrow
          'bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%236B7280\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")]',
          'bg-no-repeat bg-[right_1rem_center] bg-[length:1.25rem]',
          error
            ? 'ring-2 ring-red/30 dark:ring-red-dark/30'
            : 'border border-black/[0.06] dark:border-white/[0.06]',
          className
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>

      {error && (
        <p className="text-caption1 text-red dark:text-red-dark font-medium tracking-tight">
          {error}
        </p>
      )}

      {hint && !error && (
        <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary tracking-tight">
          {hint}
        </p>
      )}
    </div>
  )
}
